import React from 'react';
import type { KinnserPullSummary } from '../../services/api/types.gen';
import { formatDateTimeLocal } from '../../utils/dateUtils';
import { formatPullSummary } from './usePullKinnser';

/**
 * What Kinnser last sent for this sandbox, in one sentence.
 *
 * The button that asks again is not here. It was, inside a bordered tile with
 * this line, which put the agency's most-used action halfway down the settings
 * while Run sat at the top -- so the two things an operator does to an agency
 * were in different places for no reason. The action is in the frame header
 * now, and this is the line that says what the last one got.
 *
 * Reads this sandbox's own pull rather than the shared Kinnser sync record.
 * That record's API is behind a feature flag and this screen is not, so a
 * screen reading it would say nothing to everybody without the flag.
 *
 * The build line sits under it because the two dates only mean anything
 * together: a pull from this morning is not in the sandbox until something
 * builds it, and a build older than the pull is a build of the pull before it.
 * The steps below say how the last run went; this says when it was.
 */
export const KinnserPullLine: React.FC<{
  pull: KinnserPullSummary | null;
  /** When this sandbox was last built. Null means never. */
  setupAt?: string | null;
  error?: string | null;
}> = ({ pull, setupAt, error }) => (
  <div className='space-y-1'>
    <p className='text-xs text-muted-foreground'>
      <span className='font-medium text-foreground'>Kinnser data: </span>
      {pull
        ? formatPullSummary(pull)
        : 'Never pulled. A build invents everything until you pull.'}
    </p>
    <p className='text-xs text-muted-foreground'>
      <span className='font-medium text-foreground'>Last build: </span>
      {setupAt
        ? formatDateTimeLocal(setupAt)
        : 'Never built. Run setup to make one.'}
    </p>
    {error && <p className='text-sm text-red-700'>{error}</p>}
  </div>
);

export default KinnserPullLine;
